import type { DestroyOptions, Sprite, Text } from 'pixi.js';
import { Assets, Container, Texture } from 'pixi.js';
import { Signal } from 'typed-signals';
import { applyLayoutState } from 'libs/layouts/populateLayouts/applyLayoutState';
import { getLocalizedValue } from '@softgames/localization';
import { type IAPProduct } from '../../app/iap/types';
import { InventoryItem } from '../../app/resources/types';
import { ShopOfferLayout } from '../../../layouts/mainMenu/shop/ShopOfferLayout';
import { formatAmountForInventory, formatInventoryCountdown, setIconForInventory } from './utils/setIconForInventory';
import { ShopSpecialOfferLayout } from '../../../layouts/mainMenu/shop/ShopSpecialOfferLayout';
import { LoadingSpinnerView } from '../../views/LoadingSpinnerView';
import { centerElements } from '../../game/utils/centerElements';
import { createLayoutInstanceFromPath } from '../../libs/layouts/runtime/createLayoutInstance';
import { ShopLoadingOfferLayout } from '../../../layouts/mainMenu/shop/ShopLoadingOfferLayout';
import { preloadLayoutsAssets } from '../../views/preloadLayoutsAssets';
import { getLayoutClassByPath } from '../../libs/layouts/runtime/layoutModules';

enum OfferTypes {
  BasicOffer = 'basic_offer',
  SpecialOffer = 'special_offer',
}

export class ShopOfferView extends Container {
  public readonly onOfferClicked = new Signal<(product: IAPProduct) => void>();

  public readonly onOfferExpired = new Signal<() => void>();

  private offerLayout: ShopOfferLayout | ShopSpecialOfferLayout | null = null;

  constructor(product: IAPProduct) {
    super();

    if (product.data.offerPopupView) {
      void this.initPopupOffer(product);
    } else if (product.data?.shopOfferView?.layoutPath) {
      void this.initCustomLayout(product, product.data?.shopOfferView?.layoutPath);
    } else {
      this.initNormalOffer(product);
    }
  }

  private async initCustomLayout(product: IAPProduct, layoutPath: string): Promise<void> {

    const loadingLayout = new ShopLoadingOfferLayout();

    loadingLayout.root.props({
      parent: this,
    });

    await preloadLayoutsAssets([getLayoutClassByPath(layoutPath)]);

     if (this.destroyed) return;

     const layout = createLayoutInstanceFromPath(layoutPath);
     layout.root.props({ parent: this, addAtIndex: 0 });
     layout.root.onpointertap = (): void => {
       this.onOfferClicked.emit(product);
     };

    loadingLayout.root.destroy({ children: true });
  }

  private async initPopupOffer(product: IAPProduct): Promise<void> {
    if (!product.data.offerPopupView) return;

    this.offerLayout = new ShopSpecialOfferLayout();
    const { spinnerSlot, readyContainer, loadingContainer, background } = this.offerLayout;

    const { background: backgroundUrl } = product.data.offerPopupView;

    new LoadingSpinnerView().props({ parent: spinnerSlot });

    readyContainer.props({ visible: false });

    await Assets.load(backgroundUrl);

    background.props({
      texture: backgroundUrl,
    });

    loadingContainer.visible = false;
    readyContainer.visible = true;

    this.initCommon(this.offerLayout, product);
  }

  private initCommon(layout: ShopOfferLayout | ShopSpecialOfferLayout, product: IAPProduct): void {
    const { root, offerName, bannerContainer, bannerText, timerContainer, timerText } = layout;
    offerName.text = product.displayName ?? '';

    bannerContainer.visible = false;
    timerContainer.visible = false;

    if (product.cornerFlash) {
      bannerContainer.visible = true;
      bannerText.text = product.cornerFlash ?? '';
    }

    if (product.endDate) {
      timerContainer.visible = true;
      this.updateTimer(timerText, product);
      const interval = setInterval(() => {
        this.updateTimer(timerText, product);
      }, 200);

      this.once('destroyed', () => {
        clearInterval(interval);
      });
    }

    root.props({
      parent: this,
    });

    root.onpointertap = (): void => {
      this.onOfferClicked.emit(product);
    };
  }

  private initNormalOffer(product: IAPProduct): void {
    const layout = new ShopOfferLayout();
    this.offerLayout = layout;
    const { root, coinRewardText, priceLabelText } = this.offerLayout;

    root.props({
      parent: this,
    });

    if (product.tags.includes('BasicBundle')) {
      applyLayoutState(this.offerLayout, OfferTypes.BasicOffer);
    } else {
      applyLayoutState(this.offerLayout, OfferTypes.SpecialOffer);
    }
    const boosterRewards: { type: InventoryItem; amount: number }[] = [];
    const livesRewards: { type: InventoryItem; amount: number }[] = [];

    const boosterItems = [InventoryItem.BoosterHammer, InventoryItem.BoosterMove, InventoryItem.BoosterRedraw];
    const livesItems = [InventoryItem.Lives, InventoryItem.UnlimitedLivesMinutes];

    boosterItems.forEach((item) => {
      if (product.rewards[item]) {
        boosterRewards.push({ type: item, amount: product.rewards[item] || 0 });
      }
    });

    livesItems.forEach((item) => {
      if (product.rewards[item]) {
        livesRewards.push({ type: item, amount: product.rewards[item] || 0 });
      }
    });

    applyLayoutState(
      this.offerLayout.rewardsLayout,
      `booster_${boosterRewards.length}_infinite_${livesRewards.length}`,
    );
    boosterRewards.forEach((reward, index) => {
      const rewardsLayout = layout.rewardsLayout as unknown as Record<string, unknown>;
      const rewardText = rewardsLayout[`rewardAmount${(index + 1).toString().padStart(2, '0')}`] as Text;
      if (rewardText) {
        formatAmountForInventory(rewardText, reward.type, reward.amount);
      }

      const rewardIcon = rewardsLayout[`rewardIcon${(index + 1).toString().padStart(2, '0')}`] as Sprite;
      if (rewardIcon) {
        setIconForInventory(rewardIcon, reward.type);
      }

      const shadowIcon = rewardsLayout[`rewardIconShadow${(index + 1).toString().padStart(2, '0')}`] as Sprite;
      if (shadowIcon) {
        setIconForInventory(shadowIcon, reward.type);
        // after the texture was set add _Shadow to the texture name
        shadowIcon.texture = Texture.from(`${shadowIcon.texture.label}_Shadow`);
        shadowIcon.tint = product.tags.includes('BasicBundle') ? '0xcaad73' : '0x510076';
      }
    });

    livesRewards.forEach((reward, index) => {
      const rewardsLayout = layout.rewardsLayout as unknown as Record<string, unknown>;
      const rewardText = rewardsLayout[`rewardAmount${(index + 5).toString().padStart(2, '0')}`] as Text;

      if (rewardText) {
        formatAmountForInventory(rewardText, reward.type, reward.amount);
      }

      const rewardIcon = rewardsLayout[`rewardIcon${(index + 5).toString().padStart(2, '0')}`] as Sprite;
      if (rewardIcon) {
        setIconForInventory(rewardIcon, reward.type);
      }
    });

    coinRewardText.text = getLocalizedValue(product.rewards[InventoryItem.Coin] ?? 0);
    priceLabelText.text = product.priceLabel ?? '';

    this.initCommon(layout, product);
  }

  private updateTimer(timerText: Text, offer: IAPProduct): void {
    if (!offer.endDate) return;

    if (offer.endDate < Date.now()) {
      this.onOfferExpired.emit();
      this.destroy({ children: true });
      return;
    }

    formatInventoryCountdown(timerText, offer.endDate);
  }

  public legacyShowValueRibbon(): void {
    console.warn('legacyRibbonValue', this.offerLayout);
    if (!(this.offerLayout instanceof ShopOfferLayout)) return;

    const { bannerContainer, legacyRibbonValue, ribbonValueText, ribbonMultiText } = this.offerLayout;

    bannerContainer.visible = false;
    legacyRibbonValue.visible = true;
    centerElements([ribbonMultiText, ribbonValueText], 0);
  }

  public destroy(options?: DestroyOptions): void {
    this.onOfferClicked.disconnectAll();
    this.onOfferExpired.disconnectAll();
    if (this.offerLayout?.root) {
      this.offerLayout.root.onpointertap = null;
    }
    super.destroy(options);
  }
}
